// Browser push notifier — polls the in-app notifications endpoint at a low
// frequency and fires a native OS notification for locker_share_open events
// WHEN AND ONLY WHEN:
//   1. The user has granted Notification permission (via /settings/storage),
//   2. The tab is currently hidden / minimized / another tab is focused,
//   3. This particular notification hasn't been surfaced yet on this device.
//
// Design rationale:
//   • No server-side push subscription (no VAPID keys, no vendor lock-in) —
//     keeps this shippable without extra infra.
//   • The in-tab bell + sonner toast already cover the "tab is open" case,
//     so we deliberately skip pushes then to avoid double-notify (option A).
//   • Poll every 60s while hidden; back off to 0 when visible.
//   • Seen-set is persisted in localStorage so a page reload doesn't refire.

import { api } from "@/lib/api";

const SEEN_KEY  = "fn_notif_seen";
const POLL_MS   = 60_000;
// Kinds we surface as native OS notifications. Backend inserts these into
// db.user_notifications; when the tab is hidden we mirror them as a
// browser notification with a click-through link.
//   locker_share_open  — a nominee opened a shared doc
//   train_arrival      — the user's tracked train is 10 min from a station
//   food_basket_ready  — the pooled food order is at the platform / ready
const SUPPORTED_KINDS = new Set([
  "locker_share_open",
  "train_arrival",
  "food_basket_ready",
]);
const MAX_SEEN  = 200;              // LRU-ish cap on the seen-set

let timer   = null;
let started = false;

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch { return new Set(); }
}
function saveSeen(set) {
  try {
    // LRU trim — keep the newest MAX_SEEN entries.
    const arr = Array.from(set);
    if (arr.length > MAX_SEEN) arr.splice(0, arr.length - MAX_SEEN);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch { /* ignore quota errors */ }
}

async function fireOne(n) {
  try {
    const reg = navigator.serviceWorker ? await navigator.serviceWorker.getRegistration() : null;
    const opts = {
      body:  n.body || "",
      icon:  "/logo192.png",
      badge: "/logo64.png",
      tag:   `f2n-${n.notif_id}`,
      data:  { link: n.link || "/", notif_id: n.notif_id },
      renotify: false,
    };
    // Prefer showNotification via the SW registration — it survives tab
    // close on desktop Chrome/Firefox. Falls back to the raw Notification
    // constructor when no SW registration is available.
    if (reg && reg.showNotification) {
      await reg.showNotification(n.title || "find2near", opts);
    } else {
      // Legacy path — only works while at least one client is alive.
      new Notification(n.title || "find2near", opts);
    }
  } catch (e) {
    // Never let a notification error bubble.
    console.warn("[pushNotifier] fire failed", e);
  }
}

async function tick() {
  // Skip when the tab is visible — the in-app UI has it covered.
  if (document.visibilityState === "visible") return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const r = await api.get("/personal/notifications", { params: { unread_only: true } });
    // The endpoint returns either a bare list (legacy) or {items, next_cursor,
    // unread_count} (Inbox v1+). Handle both so a partial deploy doesn't break.
    const list = Array.isArray(r.data) ? r.data
                 : (Array.isArray(r.data?.items) ? r.data.items : []);
    if (!list.length) return;
    const seen = loadSeen();
    let firedAny = false;
    for (const n of list) {
      if (!SUPPORTED_KINDS.has(n.kind)) continue;
      if (seen.has(n.notif_id)) continue;
      await fireOne(n);
      seen.add(n.notif_id);
      firedAny = true;
    }
    if (firedAny) saveSeen(seen);
  } catch {
    // Silent — offline or auth expired. The next visibility change or tick
    // will retry. Never noisy.
  }
}

// Called once from App.js after auth boot. Idempotent.
export function startPushNotifier() {
  if (started) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  started = true;
  const arm = () => {
    if (timer) return;
    timer = setInterval(tick, POLL_MS);
    tick();       // fire once on arm
  };
  const disarm = () => {
    if (timer) { clearInterval(timer); timer = null; }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") arm();
    else                                       disarm();
  });
  if (document.visibilityState === "hidden") arm();
}
